import { UnsavedChangesGuard } from '../unsaved_changes_guard.js';

export const WorkspaceMethods = {
  _connectWorkspace() {
    this._connectGridSettings();
    this._workspaceGuard = new UnsavedChangesGuard({ messageValue: 'Leave the map with unsaved changes?' }, false);
    this._gridDetectionSource = (event) => this._applyDetectedGridState(event.detail);
    this.element.addEventListener('grid-detection:source', this._gridDetectionSource);
    if (this.detectGridUrlValue) {
      const url = this.detectGridUrlValue;
      this.detectGridUrlValue = '';
      const location = new URL(window.location.href);
      location.searchParams.delete('detect_grid');
      window.history.replaceState(window.history.state, '', location);
      queueMicrotask(() => {
        if (this._mapConnected) this._shapeModalFrame().src = url;
      });
    }
    this._workspaceMode ||= this.editorTarget.dataset.mode || 'editor';
    try {
      this._autosaveEnabled = localStorage.getItem('konva-map:autosave') !== 'false';
      this._toolLabels = localStorage.getItem('konva-map:tool-labels') !== 'false';
    } catch {
      this._autosaveEnabled ??= true;
      this._toolLabels = true;
    }
    this._mobileMedia = window.matchMedia('(max-width: 767.98px)');
    this._mobileViewportChanged = () => {
      this._mobileEditor = this._mobileMedia.matches;
      this._syncWorkspaceControls();
      this._refreshSelectionToolbar();
    };
    this._mobileMedia.addEventListener('change', this._mobileViewportChanged);
    this._mobileViewportChanged();
    this._workspaceKeyDown = (event) => {
      if (event.target.closest('input, textarea, select, [contenteditable]')) return;
      if (event.target.closest('dialog[open]') !== this.element.closest('dialog[open]')) return;
      if (this._gridCalibration) {
        if ((event.ctrlKey || event.metaKey) && ['z', 'y'].includes(event.key.toLowerCase())) event.preventDefault();
        return;
      }
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && key === 'z') {
        if (event.shiftKey) this.redoMapEdit(event);
        else this.undoMapEdit(event);
      } else if ((event.ctrlKey || event.metaKey) && key === 'y') this.redoMapEdit(event);
      else if ((event.ctrlKey || event.metaKey) && key === 's') this.saveMap(event);
      else if (key === 'escape' && !this._characterMove && !this._effectRotating) {
        this._cancelCanvasGesture();
        this.closeFogBrush();
        this.closeLayers();
        if (this._pendingAreaEffect || this._pendingCharacterToken) this._applyTool('select');
        else this._deselectShape();
      }
    };
    this._workspaceBeforeUnload = (event) => this._workspaceGuard.confirmUnload(event);
    this._workspaceBeforeVisit = (event) => this._workspaceGuard.confirmVisit(event);
    this._workspaceBeforeClick = (event) => {
      if (event.target.closest('a[data-turbo-frame="drawer-modal"]')) return;
      this._workspaceGuard.confirmClick(event);
    };
    const editorPath = window.location.pathname;
    this._workspaceHistory = (event) => {
      if (window.location.pathname !== editorPath || this._workspaceGuard.restoring)
        this._workspaceGuard.confirmHistory(event);
    };
    this._workspaceBeforeMorph = (event) => this._protectPositionDraft(event);
    this._workspaceBeforeClose = (event) => {
      if (event.target.contains(this.element)) this._workspaceGuard.confirmModalClose(event);
    };
    this._workspaceFullscreen = () => this._syncWorkspaceControls();
    this._workspacePointerDown = (event) => {
      if (
        !event.target.closest('[data-konva-map-target~="fogBrushPopover"], [data-konva-map-target~="fogBrushAnchor"]')
      )
        this.closeFogBrush();
    };
    document.addEventListener('keydown', this._workspaceKeyDown);
    document.addEventListener('turbo:before-visit', this._workspaceBeforeVisit);
    document.addEventListener('click', this._workspaceBeforeClick, true);
    document.addEventListener('turbo:before-morph-attribute', this._workspaceBeforeMorph);
    document.addEventListener('fullscreenchange', this._workspaceFullscreen);
    document.addEventListener('pointerdown', this._workspacePointerDown);
    document.addEventListener('modal:closing', this._workspaceBeforeClose, true);
    window.addEventListener('beforeunload', this._workspaceBeforeUnload);
    window.addEventListener('popstate', this._workspaceHistory, true);
  },

  applyStreamState(event) {
    this._applyDetectedGridState(event.detail);
    // Local map gestures and unsaved positioning retain priority over broadcasts.
    const positionDraft = this._pendingPositionJson || this._savingPositionJson || this._failedPositionJson || this._gestureSnapshot;
    for (const [name, value] of Object.entries(event.detail.position || {})) {
      if (positionDraft && ['positionZoom', 'positionPosX', 'positionPosY'].includes(name)) continue;
      this[`${name}Value`] = value;
    }
    this._onTurboMorph();
  },

  _applyDetectedGridState(state) {
    for (const target of this.mapNameOverlayTargets) target.textContent = state.name;
    if (this.hasImagePlaceholderTarget) this.imagePlaceholderTarget.hidden = !!state.image_url;
    for (const control of this.editorTarget.querySelectorAll('[data-map-image-control]'))
      control.disabled = !state.image_url;
    for (const [name, value] of Object.entries(state.grid)) {
      const property = name.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      this[`${property}Value`] = value;
    }
    if (String(state.image_id || '') !== this.reinitializeValue) {
      this.previewUrlValue = '';
      this.imageWidthValue = 0;
      this.imageHeightValue = 0;
      this.imageContentTypeValue = state.image_content_type || '';
      this.imageUrlValue = state.image_url || '';
      this.reinitializeValue = String(state.image_id || '');
    }
  },

  _disconnectWorkspace() {
    this._disconnectGridSettings();
    this.element.removeEventListener('grid-detection:source', this._gridDetectionSource);
    this._mobileMedia?.removeEventListener('change', this._mobileViewportChanged);
    document.removeEventListener('keydown', this._workspaceKeyDown);
    document.removeEventListener('turbo:before-visit', this._workspaceBeforeVisit);
    document.removeEventListener('click', this._workspaceBeforeClick, true);
    document.removeEventListener('turbo:before-morph-attribute', this._workspaceBeforeMorph);
    document.removeEventListener('fullscreenchange', this._workspaceFullscreen);
    document.removeEventListener('pointerdown', this._workspacePointerDown);
    document.removeEventListener('modal:closing', this._workspaceBeforeClose, true);
    window.removeEventListener('beforeunload', this._workspaceBeforeUnload);
    window.removeEventListener('popstate', this._workspaceHistory, true);
    this._workspaceGuard?.disconnect();
    this._selectionNode?.off('.selectionToolbar');
  },

  _restoreWorkspace() {
    if (!this.hasEditorTarget) return;
    this._syncWorkspaceControls();
    if (this._stage && this._workspaceMode === 'position' && !this._positionManagerActive)
      this._applyTool('position-manager');
  },

  _protectPositionDraft(event) {
    if (
      event.target !== this.element ||
      !(this._pendingPositionJson || this._savingPositionJson || this._failedPositionJson)
    )
      return;
    if (
      [
        'data-konva-map-position-zoom-value',
        'data-konva-map-position-pos-x-value',
        'data-konva-map-position-pos-y-value'
      ].includes(event.detail.attributeName)
    )
      event.preventDefault();
  },

  _showWorkspacePanel(toolbar, tool) {
    if (toolbar === 'position') this.switchWorkspaceMode({ currentTarget: { dataset: { mode: 'position' } } });
    else {
      if (this._workspaceMode === 'position')
        this.switchWorkspaceMode({ currentTarget: { dataset: { mode: 'editor' } } });
      if (tool && this._stage) this._applyTool(tool);
    }
  },

  switchWorkspaceMode(event) {
    event?.preventDefault?.();
    this._cancelCanvasGesture();
    this._workspaceMode = event.currentTarget.dataset.mode;
    this.closeLayers();
    this.closeFogBrush();
    this._replaceToolbarUrlParam(this._workspaceMode === 'position' ? 'position' : null);
    if (this._stage) this._applyTool(this._workspaceMode === 'position' ? 'position-manager' : 'select');
    this._syncWorkspaceControls();
  },

  toggleToolLabels() {
    this._toolLabels = !this._toolLabels;
    try {
      localStorage.setItem('konva-map:tool-labels', String(this._toolLabels));
    } catch {
      /* Optional preference. */
    }
    this._syncWorkspaceControls();
  },

  chooseToolCategory(event) {
    this._workspaceCategory = event.currentTarget.dataset.category;
    this._syncWorkspaceControls();
  },

  choosePositionCategory(event) {
    this._positionCategory = event.currentTarget.dataset.category;
    this._syncWorkspaceControls();
  },

  _syncWorkspaceControls() {
    if (!this.hasEditorTarget) return;
    const editor = this.editorTarget;
    if (this.hasToolRailTarget) this.toolRailTarget.inert = !!this._gridCalibration;
    this._workspaceMode ||= editor.dataset.mode || 'editor';
    editor.dataset.mode = this._workspaceMode;
    editor.dataset.labels = String(this._toolLabels !== false);
    editor.dataset.category = this._workspaceCategory || 'tools';
    if (this.hasAutosaveTarget) this.autosaveTarget.checked = this._autosaveEnabled !== false;
    if (this.hasToolLabelsTarget) {
      const label = this._toolLabels === false ? 'Show tool labels' : 'Hide tool labels';
      this.toolLabelsTarget.querySelector('span').textContent = label;
      this.toolLabelsTarget.setAttribute('aria-label', label);
      this.toolLabelsTarget.setAttribute('title', label);
      this.toolLabelsTarget.setAttribute('aria-pressed', String(this._toolLabels !== false));
    }
    for (const button of this.modeButtonTargets) {
      const active = button.dataset.mode === editor.dataset.mode;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    }
    for (const button of editor.querySelectorAll('.map-workspace__editor-tools nav button'))
      button.setAttribute('aria-pressed', String(button.dataset.category === editor.dataset.category));
    if (this.hasPositionFieldsTarget) this.positionFieldsTarget.dataset.category = this._positionCategory || 'position';
    for (const button of editor.querySelectorAll('.map-workspace__position-tools nav button'))
      button.setAttribute('aria-pressed', String(button.dataset.category === (this._positionCategory || 'position')));
    const placing = !!(this._pendingAreaEffect || this._pendingCharacterToken);
    if (this.hasPlacementControlsTarget) this.placementControlsTarget.hidden = !placing;
    if (this.hasPlaceEffectTarget) {
      this.placeEffectTarget.hidden = !this._pendingAreaEffect;
      this.placeEffectTarget.disabled = !this._areaEffectPlacementPoint;
    }
    if (this.hasWorkspaceInstructionTarget)
      this.workspaceInstructionTarget.textContent = placing
        ? this._pendingCharacterToken
          ? 'Click or tap a square to place your character · Escape to cancel'
          : 'Click to place an effect · On touch, preview then press Place'
        : this._workspaceMode === 'position'
          ? 'Pan or zoom to frame the game screen. Screen layout changes are live.'
          : 'Scroll to pan · Shift+scroll horizontally · Ctrl/Cmd+scroll to zoom · Ctrl/Cmd+Z to undo · Escape to cancel';
    if (this.hasFullscreenButtonTarget) {
      const label = document.fullscreenElement ? 'Exit fullscreen' : 'Enter fullscreen';
      this.fullscreenButtonTarget.setAttribute('aria-label', label);
      this.fullscreenButtonTarget.title = label;
    }
    this._syncHistoryControls?.();
    this._syncSaveStatus?.();
  },

  async toggleEditorFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await this.element.requestFullscreen();
    } catch {
      this.workspaceInstructionTarget.textContent = 'Fullscreen is unavailable in this browser.';
    }
  },

  toggleLayers() {
    if (this._mobileEditor) {
      this._shapeModalFrame().src = this.layersButtonTarget.dataset.modalUrl;
      return;
    }
    const open = this.layersPanelTarget.hidden;
    this.layersPanelTarget.hidden = !open;
    this.layersButtonTarget.setAttribute('aria-expanded', String(open));
    this.layersButtonTarget.classList.toggle('active', open);
    this._updateShapesList();
  },

  closeLayers() {
    if (this.hasLayersPanelTarget) this.layersPanelTarget.hidden = true;
    if (this.hasLayersButtonTarget) {
      this.layersButtonTarget.setAttribute('aria-expanded', 'false');
      this.layersButtonTarget.classList.remove('active');
    }
  },

  closeFogBrush(event) {
    event?.preventDefault();
    this._brushDismissed = true;
    if (this.hasFogBrushPopoverTarget) this._setToolbarPopover(this.fogBrushPopoverTarget);
  },

  _refreshSelectionToolbar() {
    if (!this.hasSelectionToolbarTarget) return;
    const shape = this._findShapeById(this._selectedShapeId);
    const visible = shape && this._tool === 'select' && !this._editingShapeId && !this._characterMove;
    this.selectionToolbarTarget.hidden = !visible;
    if (!visible) return;
    this.selectionNameTarget.textContent = shape.name;
    this.selectionNameTarget.title = shape.name;
    this.selectionMoveTarget.hidden = shape.type !== 'character';
    this.selectionMoveTarget.disabled = !this.gridEnabledValue;
    for (const button of this.selectionToolbarTarget.querySelectorAll('[data-shape-id]'))
      button.dataset.shapeId = shape.id;
    if (this._selectionNode !== shape.konvaShape) {
      this._selectionNode?.off('.selectionToolbar');
      this._selectionNode = shape.konvaShape;
      this._selectionNode.on(
        'xChange.selectionToolbar yChange.selectionToolbar widthChange.selectionToolbar heightChange.selectionToolbar scaleXChange.selectionToolbar scaleYChange.selectionToolbar',
        () => this._positionSelectionToolbar()
      );
    }
    this._positionSelectionToolbar();
  },

  _positionSelectionToolbar() {
    if (!this.hasSelectionToolbarTarget || this.selectionToolbarTarget.hidden || !this._stage) return;
    const node = this._findShapeById(this._selectedShapeId)?.konvaShape;
    if (!node) return;
    const rect = node.getClientRect();
    const toolbar = this.selectionToolbarTarget;
    const width = this.viewportTarget.clientWidth;
    const height = this.viewportTarget.clientHeight;
    const x = Math.max(8, Math.min(width - toolbar.offsetWidth - 8, rect.x + (rect.width - toolbar.offsetWidth) / 2));
    const below = rect.y + rect.height + 12;
    const y = below + toolbar.offsetHeight < height - 8 ? below : rect.y - toolbar.offsetHeight - 12;
    toolbar.style.left = `${x}px`;
    toolbar.style.top = `${Math.max(8, Math.min(height - toolbar.offsetHeight - 8, y))}px`;
  },

  deselectLayer() {
    this._deselectShape();
  },

  workspaceZoom(event) {
    if (!this._stage) return;
    const action = event.currentTarget.dataset.zoomAction;
    if (this._positionManagerActive) {
      this._monitorZoom =
        action === 'fit' ? 1 : Math.max(0.25, Math.min(2, (this._monitorZoom || 1) * (action === 'in' ? 1.25 : 0.8)));
      this._applyPositionWindowDimensions();
      this._applyPositionManagerTransform();
    } else if (action === 'fit') this._resetView();
    else this._applyZoom(this._currentScale * (action === 'in' ? 1.25 : 0.8));
  }
};
